import { useEffect, useMemo, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { Plus, DoorOpen, ChevronDown, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { isDue } from "@/lib/recurrence";
import { useMidnightRefresh } from "@/lib/useMidnight";
import { MemberAvatar } from "@/components/MemberAvatar";
import { PageHeader, EmptyState } from "@/components/shared";
import { CleaningCard } from "@/components/cleaning/CleaningCard";
import { CleaningTaskDialog } from "@/components/cleaning/CleaningTaskDialog";
import { RoomsManagerDialog } from "@/components/cleaning/RoomsManagerDialog";

const emptyForm = (room) => ({
  title: "",
  room: room || "",
  member_ids: [],
  frequency: "weekly",
  interval: 1,
  days_of_week: [],
});

const assignedTo = (t, m) => t.member_id === m.id || (t.member_ids || []).includes(m.id);

const recurOf = (t) => ({ frequency: t.frequency || "weekly", interval: t.interval || 1, days_of_week: t.days_of_week || [] });
// A task is "due" when it's time to do it again; otherwise it's recently completed (checked).
const isTaskDue = (t) => isDue(recurOf(t), t.last_done || null);

export default function Cleaning() {
  const { members } = useMembers();
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [roomsOpen, setRoomsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggleRoom = (room) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(room)) next.delete(room);
      else next.add(room);
      return next;
    });

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([api("/cleaning"), api("/rooms")]);
      setTasks(t.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setRooms(r.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useMidnightRefresh(load);

  // Room order incl. any orphan room names used by tasks but not in rooms table.
  const roomOrder = useMemo(() => {
    const names = rooms.map((r) => r.name);
    tasks.forEach((t) => {
      if (t.room && !names.includes(t.room)) names.push(t.room);
    });
    return names;
  }, [rooms, tasks]);

  const grouped = useMemo(() => {
    const map = {};
    roomOrder.forEach((n) => (map[n] = []));
    tasks.forEach((t) => {
      const key = t.room || "General";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tasks, roomOrder]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(rooms[0]?.name || ""));
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title,
      room: t.room,
      member_ids: t.member_ids?.length ? t.member_ids : t.member_id ? [t.member_id] : [],
      frequency: t.frequency,
      interval: t.interval || 1,
      days_of_week: t.days_of_week || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the task a name");
    if (!form.room) return toast.error("Pick a room");
    setSaving(true);
    const body = {
      title: form.title.trim(),
      room: form.room,
      member_ids: form.member_ids,
      member_id: null,
      frequency: form.frequency,
      interval: form.interval,
      days_of_week: form.frequency === "weekly" ? form.days_of_week : [],
    };
    try {
      if (editing) await api(`/cleaning/${editing.id}`, { method: "PUT", body });
      else await api("/cleaning", { method: "POST", body });
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Task updated" : "Task added");
    } catch {
      toast.error("Could not save task");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    try {
      await api(`/cleaning/${editing.id}`, { method: "DELETE" });
      setDialogOpen(false);
      await load();
      toast.success("Task deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const complete = async (t) => {
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, last_done: new Date().toISOString() } : x)));
    try {
      await api(`/cleaning/${t.id}/complete`, { method: "POST" });
      toast.success("Marked done ✨");
    } catch {
      load();
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || source.droppableId !== destination.droppableId) return;
    const roomName = source.droppableId;
    const roomItems = grouped[roomName] || [];
    const dueItems = roomItems.filter(isTaskDue);
    const doneItems = roomItems.filter((t) => !isTaskDue(t));
    const [moved] = dueItems.splice(source.index, 1);
    dueItems.splice(destination.index, 0, moved);
    const flat = [];
    roomOrder.forEach((n) => {
      if (n === roomName) flat.push(...dueItems, ...doneItems);
      else flat.push(...(grouped[n] || []));
    });
    setTasks(flat);
    try {
      await api("/cleaning/reorder", { method: "POST", body: { ordered_ids: flat.map((t) => t.id) } });
    } catch {
      load();
    }
  };

  // Room CRUD (UI handled by RoomsManagerDialog)
  const addRoom = async (name) => {
    try {
      await api("/rooms", { method: "POST", body: { name } });
      await load();
    } catch {
      toast.error("Could not add room");
    }
  };
  const renameRoom = async (room, name) => {
    try {
      await api(`/rooms/${room.id}`, { method: "PUT", body: { name } });
      await load();
    } catch {
      toast.error("Could not rename");
    }
  };
  const deleteRoom = async (room) => {
    try {
      await api(`/rooms/${room.id}`, { method: "DELETE" });
      await load();
    } catch {
      toast.error("Could not delete room");
    }
  };

  return (
    <div>
      <PageHeader
        title="Cleaning"
        subtitle="Keep every room fresh, room by room"
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setRoomsOpen(true)} data-testid="manage-rooms-btn">
              <DoorOpen className="mr-1.5 h-4 w-4" /> Rooms
            </Button>
            <Button className="rounded-full" onClick={openNew} data-testid="add-cleaning-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Task
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="mt-4 space-y-3">
          {["s1", "s2", "s3"].map((k) => (
            <Skeleton key={k} className="h-24 w-full rounded-3xl" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          emoji="🫧"
          title="Sparkling clean"
          message="Add cleaning tasks and organize them by room."
          action={
            <Button className="rounded-full" onClick={openNew}>
              <Plus className="mr-1.5 h-4 w-4" /> Add a task
            </Button>
          }
        />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="mt-5 space-y-5">
            {roomOrder.map((room) => {
              const items = grouped[room] || [];
              if (!items.length) return null;
              const dueItems = items.filter(isTaskDue);
              const doneItems = items.filter((t) => !isTaskDue(t));
              const isCollapsed = collapsed.has(room);
              return (
                <div key={room} data-testid={`room-section-${room}`}>
                  <button
                    type="button"
                    onClick={() => toggleRoom(room)}
                    data-testid={`room-collapse-${room}`}
                    className="mb-2 flex w-full items-center gap-2 rounded-xl px-1 py-1 text-left hover:bg-secondary/50"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                    <h2 className="flex-1 font-display text-lg font-semibold text-ink">{room}</h2>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                      {dueItems.length} to do
                    </span>
                  </button>

                  {!isCollapsed && (
                    <>
                      <Droppable droppableId={room}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2.5">
                            {dueItems.map((t, index) => (
                              <Draggable key={t.id} draggableId={t.id} index={index}>
                                {(dp, snapshot) => (
                                  <CleaningCard
                                    task={t}
                                    assignees={members.filter((m) => assignedTo(t, m))}
                                    dp={dp}
                                    snapshot={snapshot}
                                    onComplete={complete}
                                    onEdit={openEdit}
                                  />
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>

                      {doneItems.length > 0 && (
                        <div className="mt-2.5 space-y-2.5 opacity-70">
                          {doneItems.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => openEdit(t)}
                              data-testid={`cleaning-done-${t.id}`}
                              className="bento-tile flex w-full items-center gap-3 p-4 text-left"
                            >
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-mint bg-mint text-white">
                                <Check className="h-5 w-5" strokeWidth={3} />
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-display font-semibold text-ink line-through">{t.title}</p>
                                <p className="text-xs text-muted-foreground">Done — resets when next due</p>
                              </div>
                              <div className="flex -space-x-2">
                                {members.filter((m) => assignedTo(t, m)).slice(0, 3).map((m) => (
                                  <MemberAvatar key={m.id} member={m} size="sm" />
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      <CleaningTaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        members={members}
        roomOrder={roomOrder}
        saving={saving}
        onSave={save}
        onDelete={remove}
      />

      <RoomsManagerDialog
        open={roomsOpen}
        onOpenChange={setRoomsOpen}
        rooms={rooms}
        onAddRoom={addRoom}
        onRenameRoom={renameRoom}
        onDeleteRoom={deleteRoom}
      />
    </div>
  );
}
